import type {
  AuditQuery,
  AuditResult,
  CreateOrganizationBody,
  IssuedLink,
  ListPlatformUsersQuery,
  OrganizationEntry,
  Paginated,
  PlatformUserEntry,
  UpdateOrganizationBody,
} from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryString } from '../../lib/api.js';

const organizationsKey = ['plataforma', 'organizacoes'] as const;
const usersKey = ['plataforma', 'usuarios'] as const;

export function useOrganizations(search?: string) {
  return useQuery({
    queryKey: [...organizationsKey, search ?? ''],
    queryFn: ({ signal }) =>
      api.get<{ items: OrganizationEntry[] }>(
        `/plataforma/organizacoes${queryString({ search: search || undefined })}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: UpdateOrganizationBody & { id: string }) =>
      api.patch<OrganizationEntry>(`/plataforma/organizacoes/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationsKey }),
  });
}

/**
 * A trilha de uma organização, lida de fora dela.
 *
 * Mesma resposta da auditoria de dentro do órgão — o servidor reaproveita o
 * serviço com o escopo trocado —, então a tela pode ser a mesma.
 */
export function useOrganizationAudit(organizationId: string | null, query: Partial<AuditQuery>) {
  return useQuery({
    queryKey: [...organizationsKey, organizationId, 'auditoria', query],
    enabled: organizationId !== null,
    queryFn: ({ signal }) =>
      api.get<AuditResult>(
        `/plataforma/organizacoes/${organizationId}/auditoria${queryString({
          page: query.page ?? 1,
          perPage: query.perPage ?? 30,
          search: query.search,
        })}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateOrganizationBody) =>
      api.post<{ organization: OrganizationEntry; link: IssuedLink }>(
        '/plataforma/organizacoes',
        body,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationsKey }),
  });
}

export function usePlatformUsers(query: Partial<ListPlatformUsersQuery>) {
  return useQuery({
    queryKey: [...usersKey, query],
    queryFn: ({ signal }) =>
      api.get<Paginated<PlatformUserEntry>>(
        `/plataforma/usuarios${queryString({
          page: query.page ?? 1,
          perPage: query.perPage ?? 50,
          search: query.search,
          organizationId: query.organizationId,
        })}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function usePlatformPasswordLink() {
  return useMutation({
    mutationFn: (userId: string) => api.post<IssuedLink>(`/plataforma/usuarios/${userId}/senha`),
  });
}

export function useSetPlatformUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: 'active' | 'suspended' }) =>
      api.patch<{ ok: true }>(`/plataforma/usuarios/${userId}/situacao`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}
